import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    IconButton,
    Tooltip,
    Typography,
    Pagination,
} from '@mui/material';
import { Visibility, Add } from '@mui/icons-material';
import { StockTransferService } from '../../services/stockTransfer.service';
import {
    StockTransferDTO,
    StockTransferStatus,
    STOCK_TRANSFER_STATUS_LABELS,
} from '../../interfaces/stockTransfer.interface';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd',
};

const STATUS_OPTIONS: StockTransferStatus[] = [
    'DRAFT',
    'PENDING_APPROVAL',
    'COMPLETED',
    'CANCELLED',
];

const StockTransferList: React.FC = () => {
    const navigate = useNavigate();
    const [transfers, setTransfers] = useState<StockTransferDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0,
    });

    const handleViewDetails = (id: string) => {
        navigate(`/stock-transfers/${id}`);
    };

    const fetchTransfers = async (page: number = 0, status: string = statusFilter) => {
        try {
            setLoading(true);
            const response = await StockTransferService.getStockTransfersByInstitution(status, page);
            setTransfers(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements,
            });
            setError('');
        } catch (err) {
            setError('Failed to load stock transfers');
            console.error('Error fetching stock transfers:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransfers(0, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handleStatusChange = (event: SelectChangeEvent) => {
        setStatusFilter(event.target.value);
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchTransfers(page - 1, statusFilter);
    };

    return (
        <div style={{ padding: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Stock Transfers</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/stock-transfers/create')}
                >
                    New Transfer
                </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={handleStatusChange}>
                        <MenuItem value="">All</MenuItem>
                        {STATUS_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>
                                {STOCK_TRANSFER_STATUS_LABELS[status]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    Total: {pageInfo.totalElements}
                </Typography>
            </Box>

            {loading ? (
                <div data-testid="loading-state">Loading...</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }} data-testid="stock-transfers-table">
                    <thead>
                        <tr>
                            <th style={tableHeaderStyle}>Reference No</th>
                            <th style={tableHeaderStyle}>From Store</th>
                            <th style={tableHeaderStyle}>To Store</th>
                            <th style={tableHeaderStyle}>Date</th>
                            <th style={tableHeaderStyle}>Status</th>
                            <th style={tableHeaderStyle}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transfers.length === 0 ? (
                            <tr>
                                <td style={tableCellStyle} colSpan={6} align="center">
                                    No stock transfers found
                                </td>
                            </tr>
                        ) : (
                            transfers.map((transfer) => (
                                <tr key={transfer.id} data-testid={`stock-transfer-row-${transfer.id}`}>
                                    <td style={tableCellStyle}>{transfer.referenceNo}</td>
                                    <td style={tableCellStyle}>{transfer.fromStoreName || '-'}</td>
                                    <td style={tableCellStyle}>{transfer.toStoreName || '-'}</td>
                                    <td style={tableCellStyle}>
                                        {transfer.transferDate ? formatDate(transfer.transferDate) : '-'}
                                    </td>
                                    <td style={tableCellStyle}>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-block',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                bgcolor: 'warning.light',
                                                color: 'warning.dark',
                                            }}
                                        >
                                            {STOCK_TRANSFER_STATUS_LABELS[transfer.status] ?? transfer.status}
                                        </Box>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <Tooltip title="View Details" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewDetails(transfer.id)}
                                                color="primary"
                                                data-testid={`view-button-${transfer.id}`}
                                                aria-label="View Details"
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {error && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                    {error}
                </Box>
            )}
        </div>
    );
};

export default StockTransferList;
